import { back, backToDetail, navigate, stackDepth, type Route } from '@/app'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BRAUZER TARİXÇƏSİ — ünvan (#/…) və «Geri» düyməsi (yalnız tv.kinobox.az)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠⚠ NİYƏ VAR (2026-09-25, istifadəçi testi): «TV-də ümumiyyətlə linklər yoxdur,
 *    səhifəni refresh edəndə baş səhifəyə qayıdır» və «TV-də bir dəfə back
 *    vurdunsa avtomatik sayt bağlanır».
 *
 *    ÖLÇÜLDÜ: `app.ts` marşrutu yaddaşdakı YIĞINDIR, ünvana və brauzer tarixçəsinə
 *    heç nə yazmır (cihaz app-ı üçün qəsdən — orada `pushState` ya yoxdur, ya da
 *    pultun «Geri»sini app-dan çıxarır). Brauzerdə isə nəticə iki şikayətdir:
 *      · yeniləmə həmişə ilk ekrana düşür (ünvan `/`-dır, bərpa ediləcək heç nə yox);
 *      · brauzerin öz «Geri»si (TV brauzerinin fiziki düyməsi, Alt+←, sürüşdürmə)
 *        heç vaxt `keydown` kimi səhifəyə çatmır — tarixçədə tək bənd olduğu üçün
 *        ilk basış saytdan əvvəlki səhifəyə (və ya boş tab-a) qayıdır.
 *
 * HƏLL: brauzer hədəfində tarixçə YIĞINI GÜZGÜLƏYİR.
 *   · Hər ekranın hash ünvanı var (`#/browse`, `#/person/slug`, `#/list/collection/x`),
 *     film modalı isə `#/film/slug` yazır — yeniləmə həmin ekranı (və modalı) bərpa
 *     edir (`restoreFromLocation`).
 *   · `navigate` (push) → `pushState`, `navigate(…, true)` (replace) → `replaceState`,
 *     app-daxili `back()` → `history.go(-n)`; yəni brauzerin bənd sayı yığının
 *     dərinliyi ilə eynidir.
 *   · Boot-da ALTDA BİR QORUYUCU BƏND qalır (`kb: 0`): kökdə «Geri» ona düşür və
 *     dərhal geri itələnir — sayt bağlanmır, ekran dəyişmir (`app.ts`-dəki «kökdə
 *     Back udulur» qərarının brauzer qarşılığı).
 *   · Brauzerin «Geri»si (`popstate`) app-ın `back()`-inə çevrilir: modal açıqdırsa
 *     əvvəlcə modal bağlanır, oynadıcıdadırsa filmin modalına qayıdılır
 *     (`backToDetail`) — pultun «Geri»si ilə eyni davranış.
 *
 * ⚠ HASH, YOL DEYİL (`#/browse`, `/browse` yox): build `base: './'` ilə yığılır və
 *   `nginx` fallback-ı `/browse/assets/…` sorğusunu `index.html` ilə cavablayardı;
 *   hash heç bir server marşrutuna toxunmur.
 * ⚠ CİHAZ BUNDLE-INA DÜŞMÜR: bu modulu yalnız `browser/*` import edir.
 * ⚠ ES2015 HƏDDİ (`??`/`?.` yoxdur) — TV brauzer hədəfinin ümumi qaydası.
 */

interface HistoryState {
  /** Yığının dərinliyi (0 = qoruyucu bənd). */
  kb: number
  /** Açıq film modalının slug-u — ünvan `#/film/slug` olanda. */
  modal?: string
}

/** Film modalının körpüsü — `main.ts` boot-da qeyd edir (dairəvi import olmasın). */
interface ModalBridge {
  isOpen: () => boolean
  close: () => void
  /** Ünvandan film açır — YAŞ QAPISI İLƏ (`detailModal.ts` → `openWebDetailGated`). */
  openBySlug: (slug: string) => void
  /** Ən üstdəki yaş pəncərəsini (`.web-modal`) bağlayır; bağlayıbsa `true`. */
  closeDialog?: () => boolean
}

let bridge: ModalBridge | null = null
/** Son mount olunmuş marşrut — `popstate`-də oynadıcı/kök qərarı üçün. */
let lastRoute: Route | null = null
let lastDepth = 0
/** Bizim öz `history.go()` çağırışımızdan gələcək `popstate`-lərin sayı — app-a ötürülmür. */
let ignorePops = 0
/** Növbəti mount `popstate`-dən gəlir — tarixçəyə YAZMA (bənd artıq oradadır). */
let fromPop = false
/** `popstate` içindəyik — modalın bağlanması ünvana yazmasın. */
let inPop = false

export function setModalBridge(next: ModalBridge): void {
  bridge = next
}

// ── Ünvan ↔ marşrut ────────────────────────────────────────────────────────

function enc(value: string): string {
  return encodeURIComponent(value)
}

function dec(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch (error) {
    void error
    return value
  }
}

/** Marşrutun hash ünvanı. */
export function hashOf(route: Route): string {
  switch (route.name) {
    case 'detail':
      return '#/detail/' + enc(route.slug)
    case 'person':
      return '#/person/' + enc(route.slug)
    case 'player':
      return '#/player/' + enc(route.slug)
    case 'list':
      return '#/list/' + route.kind + (route.slug ? '/' + enc(route.slug) : '')
    default:
      return '#/' + route.name
  }
}

/**
 * Hash-dən marşrut. Bərpa edilə bilməyən ünvanlar `null` qaytarır:
 *   · `#/film/slug` və `#/player/slug` — modaldır (`filmFromHash`), ekran deyil;
 *   · `#/qr`, `#/profiles` — giriş axını `firstRoute()`-un işidir.
 */
export function routeFromHash(hash: string): Route | null {
  const path = hash.charAt(0) === '#' ? hash.slice(1) : hash
  const parts = path.split('/').filter(function (part) {
    return part.length > 0
  })
  const head = parts[0]
  if (!head) return null
  switch (head) {
    case 'home':
    case 'live':
    case 'search':
    case 'browse':
    case 'collections':
    case 'upcoming':
    case 'profile':
      return { name: head }
    case 'detail':
      return parts[1] ? { name: 'detail', slug: dec(parts[1]) } : null
    case 'person':
      return parts[1] ? { name: 'person', slug: dec(parts[1]) } : null
    case 'list': {
      const kind = parts[1]
      if (kind !== 'collection' && kind !== 'popular' && kind !== 'genre' && kind !== 'watchlist') return null
      const slug = parts[2] ? dec(parts[2]) : undefined
      return slug ? { name: 'list', kind: kind, slug: slug } : { name: 'list', kind: kind }
    }
    default:
      return null
  }
}

/** `#/film/slug` və `#/player/slug` → filmin slug-u (modal bərpası üçün). */
export function filmFromHash(hash: string): string | null {
  const match = /^#\/(?:film|player)\/([^/]+)$/.exec(hash)
  return match && match[1] ? dec(match[1]) : null
}

// ── Tarixçə ─────────────────────────────────────────────────────────────────

function state(): HistoryState | null {
  const raw = window.history.state as HistoryState | null
  return raw && typeof raw.kb === 'number' ? raw : null
}

function write(replace: boolean, next: HistoryState, url: string): void {
  try {
    if (replace) window.history.replaceState(next, '', url)
    else window.history.pushState(next, '', url)
  } catch (error) {
    // Köhnə mühərrikdə tarixçə API-si sınsa app onsuz işləməyə davam edir.
    void error
  }
}

/** Boot-da bir dəfə — qoruyucu bənd + `popstate` dinləyicisi. */
export function initWebHistory(): void {
  write(true, { kb: 0 }, window.location.pathname + window.location.search + window.location.hash)
  window.addEventListener('popstate', onPop, false)
}

/**
 * Hər mount-da (`browser/shell.ts` → `applyRoute`) — tarixçəni yığınla eyniləşdirir.
 */
export function historyOnRoute(route: Route): void {
  const depth = stackDepth()
  lastRoute = route
  if (fromPop) {
    // Bənd artıq oradadır (brauzer özü keçdi) — yalnız ünvanı dəqiqləşdiririk.
    fromPop = false
    write(true, { kb: depth }, hashOf(route))
    lastDepth = depth
    return
  }
  if (depth > lastDepth) {
    write(false, { kb: depth }, hashOf(route))
  } else if (depth === lastDepth) {
    write(true, { kb: depth }, hashOf(route))
  } else {
    // App-daxili «geri» (Esc · «Geri» düyməsi · `detailOverHome`): brauzer də geri gedir;
    // gələn `popstate` bizimdir, app-a ötürülmür. Düşdüyümüz bəndin ünvanı `onPop`-da
    // dəqiqləşdirilir (kökdəki ekran `replace` ilə dəyişmiş ola bilər).
    ignorePops += 1
    window.history.go(depth - lastDepth)
  }
  lastDepth = depth
}

/** Film modalı açıldı — ünvan `#/film/slug` olur (bənd əlavə edilmir, yalnız ünvan). */
export function historyModalOpened(slug: string): void {
  if (inPop) return
  write(true, { kb: lastDepth, modal: slug }, '#/film/' + enc(slug))
}

/** Film modalı bağlandı — ekranın öz ünvanı geri qayıdır. */
export function historyModalClosed(): void {
  if (inPop || !lastRoute) return
  write(true, { kb: lastDepth }, hashOf(lastRoute))
}

/**
 * Yeniləmə/dərin keçid: ünvandakı ekranı (və modalı) açır. Açıbsa `true`.
 * Yalnız giriş və profil seçimi tamam olanda çağırılır (`main.ts` → `firstRoute`).
 */
export function restoreFromLocation(): boolean {
  const hash = window.location.hash
  if (!hash || hash === '#/' || hash === '#/home') return false
  const film = filmFromHash(hash)
  if (film) {
    navigate({ name: 'home' })
    if (bridge) bridge.openBySlug(film)
    return true
  }
  const route = routeFromHash(hash)
  if (!route) return false
  navigate(route)
  return true
}

function repushCurrent(): void {
  if (!lastRoute) return
  const modalOpen = bridge ? bridge.isOpen() : false
  const st = state()
  const slug = modalOpen && st && st.modal ? st.modal : null
  if (slug) write(false, { kb: lastDepth, modal: slug }, '#/film/' + enc(slug))
  else write(false, { kb: lastDepth }, hashOf(lastRoute))
}

function onPop(event: PopStateEvent): void {
  if (ignorePops > 0) {
    ignorePops -= 1
    // Bizim öz `go()`-muz — düşdüyümüz bəndin ünvanını cari ekranla eyniləşdir.
    if (lastRoute) write(true, { kb: lastDepth }, hashOf(lastRoute))
    return
  }

  const target = (event.state as HistoryState | null) || null
  const targetDepth = target && typeof target.kb === 'number' ? target.kb : 0
  const depth = stackDepth()

  inPop = true
  try {
    // 0. Ünvan ƏL İLƏ dəyişdirilib (bizim bəndimiz deyil — `state` yoxdur): ünvandakı
    //    ekranı/filmi açırıq, sanki dərin keçiddir. Ekran `replace` ilə açılır ki,
    //    bənd sayı yığınla eyni qalsın.
    if (!target) {
      const film = filmFromHash(window.location.hash)
      if (film && bridge) {
        write(true, { kb: depth, modal: film }, '#/film/' + enc(film))
        lastDepth = depth
        bridge.openBySlug(film)
        return
      }
      const route = routeFromHash(window.location.hash)
      if (route) {
        if (bridge && bridge.isOpen()) bridge.close()
        fromPop = true
        navigate(route, true)
        return
      }
      repushCurrent()
      return
    }

    /*
      0b. 2026-10-23: YAŞ PƏNCƏRƏSİ AÇIQDIR → brauzerin «Geri»si onu bağlayır, ekran dəyişmir.
          NƏ TAPILDI: `.web-modal` (yaş bildirişi · doğum tarixi seçicisi) `popstate`-də
          bağlanmırdı — `back()` ekranı dəyişirdi, pəncərə isə yeni ekranın üstündə qalıb
          oxları içinə bağlayırdı (`shell.ts` → `focusScope`). Pultun «Geri»si ilə eyni qayda:
          əvvəl ən üstdəki qat bağlanır (`browser/ui.ts` → `closeTopWebModal`).
    */
    if (bridge && bridge.closeDialog && bridge.closeDialog()) {
      repushCurrent()
      return
    }

    // 1. Modal açıqdır → «Geri» modalı bağlayır, ekran dəyişmir; bənd geri qoyulur.
    if (bridge && bridge.isOpen()) {
      bridge.close()
      write(false, { kb: depth }, lastRoute ? hashOf(lastRoute) : '#/home')
      return
    }

    // 2. İrəli (brauzerin «İrəli»si) — ünvandan ekranı yenidən açırıq.
    if (targetDepth > depth) {
      const film = filmFromHash(window.location.hash)
      if (film && bridge) {
        // Eyni ekran üstündə modal — bənd artıq var, yalnız modalı açırıq.
        window.history.replaceState({ kb: depth, modal: film }, '', '#/film/' + enc(film))
        lastDepth = depth
        bridge.openBySlug(film)
        return
      }
      const route = routeFromHash(window.location.hash)
      if (route) {
        fromPop = true
        navigate(route)
        return
      }
      repushCurrent()
      return
    }

    // 3. Geri — app-ın öz «geri»si: oynadıcıdan filmin modalına, qalan ekranlarda
    //    bir addım yığından. Kökdə (dərinlik 1) yığın dəyişmir → bənd geri itələnir.
    const before = depth
    fromPop = true
    // 2026-10-04: brauzerin öz Geri-si hero treyleri/canlını film modalına göndərirdi,
    // player düyməsi isə ana ekrana qaytarırdı. Hər iki giriş eyni qaydanı saxlayır.
    if (lastRoute && lastRoute.name === 'player' && !lastRoute.fromHero && !lastRoute.live) backToDetail(lastRoute.slug)
    else back()
    if (stackDepth() === before) {
      // Kökdə idik (və ya oynadıcı kökdədir) — brauzer qoruyucu bəndə düşdü, geri qoyuruq.
      fromPop = false
      repushCurrent()
    }
  } finally {
    inPop = false
  }
}
